// Drop-in replacement for `useState<boolean>(false)` that protects modal /
// disclosure open-state across Chrome tab switches.
//
// Two protections, layered:
//
//   1. **In-flight close suppression.** When a `setOpen(false)` call arrives
//      during a tab transition (tab is hidden, OR within ~1500ms of a
//      visibility/focus/blur transition), the call is silently ignored.
//      Radix's Dialog occasionally fires close events on tab return when
//      the browser briefly lands focus on `body` — this swallows them.
//
//   2. **sessionStorage persistence.** If the parent component unmounts
//      during the tab switch (which we've observed in this app, despite
//      not being able to find the exact cause — likely Suspense or
//      provider value churn), the open flag is reread from storage on
//      remount. So the modal reappears even if the React tree was
//      momentarily torn down.
//
// Usage:
//
//   const [open, setOpen] = useTabSafeOpen('pa-team-modal');  // persisted
//   const [open, setOpen] = useTabSafeOpen();                  // in-memory only
//
// Pass a stable string key to enable persistence. Omit for in-memory
// behaviour (still gets close-suppression). Multiple components using the
// same key will share state — pick keys carefully.

import { useCallback, useEffect, useState } from 'react';

// Module-scoped tab-transition tracker. One global listener — every consumer
// reads from the same timestamps so we don't pile up identical listeners.
let lastTransitionAt = 0;
if (typeof document !== 'undefined') {
  const bump = () => { lastTransitionAt = Date.now(); };
  document.addEventListener('visibilitychange', bump);
  window.addEventListener('focus', bump);
  // blur counts too — covers the leading edge of a tab switch where a
  // close event might fire after blur but before the tab fully hides.
  window.addEventListener('blur', bump);
}
const TAB_TRANSITION_GRACE_MS = 1500;

function inTabTransition(): boolean {
  // document.hidden is a hard signal — the tab is currently backgrounded.
  if (typeof document !== 'undefined' && document.hidden) return true;
  // Fall back to the timestamp grace for the racy event-ordering case
  // (focus fires before visibilitychange, and vice versa).
  return Date.now() - lastTransitionAt < TAB_TRANSITION_GRACE_MS;
}

function readPersisted(key: string | undefined): boolean {
  if (!key || typeof sessionStorage === 'undefined') return false;
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}

function writePersisted(key: string | undefined, value: boolean) {
  if (!key || typeof sessionStorage === 'undefined') return;
  try {
    if (value) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch { /* private mode etc — best effort */ }
}

// Generic version: same protection model as `useTabSafeOpen` but for any
// JSON-serialisable value. Useful for persisting form drafts inside modals
// — when TeamRoute / TemplatesRoute remounts on tab switch, the form
// fields would otherwise reset to defaults; this keeps them across the
// remount.
//
// Tradeoffs vs `useState`:
// - Every set call serialises through JSON.stringify. Fine for small
//   objects (a few KB), don't use for huge values.
// - Function-form setters (`setX(prev => ...)`) work the same way as
//   useState — the previous value is the live state, not stale storage.
// - `clear()` returned helper drops the persisted entry. Call on
//   intentional reset (successful save, dialog dismiss button) so a
//   fresh open lands clean.
export function useTabSafeState<T>(
  storageKey: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void, () => void] {
  const [value, setValueRaw] = useState<T>(() => {
    if (typeof sessionStorage === 'undefined') return defaultValue;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueRaw((prev) => {
        const resolved = typeof next === 'function'
          ? (next as (p: T) => T)(prev)
          : next;
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(resolved));
        } catch {
          /* private mode etc — best effort */
        }
        return resolved;
      });
    },
    [storageKey],
  );

  const clear = useCallback(() => {
    try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
    setValueRaw(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  return [value, setValue, clear];
}

export function useTabSafeOpen(
  storageKey?: string,
): [boolean, (next: boolean) => void] {
  const [open, setOpenRaw] = useState<boolean>(() => readPersisted(storageKey));

  const setOpen = useCallback(
    (next: boolean) => {
      // Tab-transition close guard. true-sets always pass through (we never
      // want to block opening); false-sets during a transition are dropped.
      if (!next && inTabTransition()) {
        return;
      }
      writePersisted(storageKey, next);
      setOpenRaw(next);
    },
    [storageKey],
  );

  // Belt-and-braces: if the persisted value is true on mount but our
  // local state somehow defaulted to false (StrictMode double-mount race,
  // hydration mismatch), correct it once on first effect tick.
  useEffect(() => {
    if (storageKey && readPersisted(storageKey) && !open) {
      setOpenRaw(true);
    }
    // intentionally only on mount — subsequent storage changes are
    // already handled via setOpen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [open, setOpen];
}
